import { ReactNode, useMemo, useEffect } from 'react'
import { ChakraProvider, createSystem, defaultConfig } from '@chakra-ui/react'
import { useAgencyConfig } from '../hooks/useAgency'

interface AgencyProviderProps {
  children: ReactNode
}

/**
 * AgencyProvider - Loads agency configuration and applies custom theme
 *
 * This provider wraps the ChakraProvider and dynamically creates a theme
 * based on the user's agency configuration (colors, logos, etc.)
 */
export function AgencyProvider({ children }: AgencyProviderProps) {
  const { data: agencyConfig, isLoading } = useAgencyConfig()

  // Create dynamic Chakra system with agency colors
  // Force re-computation when agencyConfig.id changes
  const system = useMemo(() => {
    if (!agencyConfig || isLoading) {
      // Return default Kopro system while loading or not authenticated
      return createSystem(defaultConfig, {
        theme: {
          tokens: {
            colors: {
              brand: {
                50: { value: '#ECFDF5' },
                100: { value: '#D1FAE5' },
                200: { value: '#A7F3D0' },
                300: { value: '#6EE7B7' },
                400: { value: '#34D399' },
                500: { value: '#10B981' },
                600: { value: '#059669' },
                700: { value: '#047857' },
                800: { value: '#065F46' },
                900: { value: '#064E3B' },
                950: { value: '#022C22' },
              },
            },
            fonts: {
              heading: { value: 'Manrope Variable, system-ui, sans-serif' },
              body: { value: 'Inter, system-ui, sans-serif' },
            },
          },
          semanticTokens: {
            colors: {
              colorPalette: {
                solid: { value: '{colors.brand.600}' },
                contrast: { value: 'white' },
                fg: { value: '{colors.brand.700}' },
                muted: { value: '{colors.brand.100}' },
                subtle: { value: '{colors.brand.50}' },
                emphasized: { value: '{colors.brand.700}' },
                focusRing: { value: '{colors.brand.500}' },
              },
            },
          },
        },
      })
    }

    // Create system with agency's custom colors
    return createSystem(defaultConfig, {
      theme: {
        tokens: {
          colors: {
            brand: {
              50: { value: agencyConfig.theme.brand[50] },
              100: { value: agencyConfig.theme.brand[100] },
              200: { value: agencyConfig.theme.brand[200] },
              300: { value: agencyConfig.theme.brand[300] },
              400: { value: agencyConfig.theme.brand[400] },
              500: { value: agencyConfig.theme.brand[500] },
              600: { value: agencyConfig.theme.brand[600] },
              700: { value: agencyConfig.theme.brand[700] },
              800: { value: agencyConfig.theme.brand[800] },
              900: { value: agencyConfig.theme.brand[900] },
              950: { value: agencyConfig.theme.brand[950] },
            },
          },
          fonts: {
            heading: { value: 'Manrope Variable, system-ui, sans-serif' },
            body: { value: 'Inter, system-ui, sans-serif' },
          },
        },
        semanticTokens: {
          colors: {
            colorPalette: {
              solid: { value: '{colors.brand.600}' },
              contrast: { value: 'white' },
              fg: { value: '{colors.brand.700}' },
              muted: { value: '{colors.brand.100}' },
              subtle: { value: '{colors.brand.50}' },
              emphasized: { value: '{colors.brand.700}' },
              focusRing: { value: '{colors.brand.500}' },
            },
          },
        },
      },
    })
  }, [agencyConfig?.id, isLoading]) // Use agencyConfig.id to detect actual changes

  // Update CSS variables when agency config changes
  useEffect(() => {
    const root = document.documentElement

    if (!agencyConfig) {
      // User is not authenticated or agency not loaded yet
      // Reset to Kopro default colors
      root.style.setProperty('--agency-primary', '#059669')
      root.style.setProperty('--agency-primary-light', '#D1FAE5')
      root.style.setProperty('--agency-primary-dark', '#047857')
      document.title = 'Kopro - Gestion de copropriétés'
      return
    }

    // Set custom CSS variables for agency colors
    root.style.setProperty('--agency-primary', agencyConfig.theme.brand[500])
    root.style.setProperty('--agency-primary-light', agencyConfig.theme.brand[100])
    root.style.setProperty('--agency-primary-dark', agencyConfig.theme.brand[700])

    // Set document title with agency name
    document.title = `${agencyConfig.name} - Kopro`

    // Set favicon if available
    if (agencyConfig.assets.logoCompactUrl) {
      const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link')
      link.type = 'image/x-icon'
      link.rel = 'shortcut icon'
      link.href = agencyConfig.assets.logoCompactUrl
      if (!document.querySelector("link[rel*='icon']")) {
        const head = document.getElementsByTagName('head')[0]
        if (head) {
          head.appendChild(link)
        }
      }
    }
  }, [agencyConfig?.id]) // Use agencyConfig.id to detect actual changes

  // No need to show loading or error state
  // If user is not authenticated (login page), simply use default Kopro theme
  // If user is authenticated but agency loading, use default theme temporarily
  // If agency is loaded, use agency's custom theme
  return (
    <ChakraProvider
      key={agencyConfig?.id || 'kopro-default'}
      value={system}
    >
      {children}
    </ChakraProvider>
  )
}
